import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

type ToastVariant = 'default' | 'success' | 'warning' | 'error';

interface ToastEntry {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
}

interface PushToastArgs {
  title: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ToastContextValue {
  toasts: ToastEntry[];
  pushToast: (args: PushToastArgs) => number;
  dismissToast: (id: number) => void;
}

const toastClasses: Record<ToastVariant, string> = {
  default: 'border-border bg-surface text-text',
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  error: 'border-danger/30 bg-danger/10 text-danger',
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextToastIdRef = useRef(1);
  const timeoutHandlesRef = useRef<Record<number, number>>({});

  const dismissToast = (id: number) => {
    window.clearTimeout(timeoutHandlesRef.current[id]);
    delete timeoutHandlesRef.current[id];
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const pushToast = ({ title, description, variant = 'default', durationMs = 5000 }: PushToastArgs): number => {
    const toastId = nextToastIdRef.current;
    nextToastIdRef.current += 1;

    setToasts((prev) => [...prev, { id: toastId, title, description, variant }]);
    timeoutHandlesRef.current[toastId] = window.setTimeout(() => {
      dismissToast(toastId);
    }, durationMs);

    return toastId;
  };

  const value = useMemo<ToastContextValue>(() => ({
    toasts,
    pushToast,
    dismissToast,
  }), [toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return value;
};

export const ToastViewport: React.FC<{ className?: string }> = ({ className }) => {
  const context = useContext(ToastContext);
  if (!context || context.toasts.length === 0) return null;

  return (
    <div className={cn('fixed right-4 top-4 z-[60] flex w-full max-w-sm flex-col gap-2', className)}>
      {context.toasts.map((toast) => (
        <div key={toast.id} className={cn('rounded-md border px-3 py-2 shadow-card', toastClasses[toast.variant])}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">{toast.title}</p>
              {toast.description && (
                <p className="mt-1 text-xs opacity-80">{toast.description}</p>
              )}
            </div>
            <button
              type="button"
              className="text-xs opacity-70 hover:opacity-100"
              onClick={() => context.dismissToast(toast.id)}
              aria-label="Dismiss toast"
            >
              Close
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
